import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAuth} from "../../../providers/AuthenticationProvider";
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import "./EquipmentReport.css";
import {
    Chart,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartLegend,
    ChartSeries,
    ChartSeriesItem,
    ChartTitle,
    ChartValueAxis,
    ChartValueAxisItem
} from "@progress/kendo-react-charts";
import {pathianDataApiService} from "../../../services/PathianDataApiService";
import {EquipmentReportForm} from "./EquipmentReportForm";
import {equipmentService} from "../../../services/equipmentService";
import moment from "moment";
import {Dimmer, Loader} from "semantic-ui-react";
import {SemanticToastContainer, toast} from "react-semantic-toasts";

const WEB_PAGE = 'Equipment Report';

const toastSection = {
    marginTop: '2rem',
    whiteSpace: 'pre',
    width: '50%',
};

export const EquipmentReport = () => {
    const {id} = useParams();
    const {checkAuth, getUserType} = useAuth();

    const [loading, setLoading] = useState(false);
    const [equipment, setEquipment] = useState({});
    const [energyReportData, setEnergyReportData] = useState([]);
    const [reportStartDate, setReportStartDate] = useState(null);
    const [reportEndDate, setReportEndDate] = useState(null);

    const navigate = useNavigate();

    const colors = [
        '#E2431E',
        '#6F9654',
        '#1C91C0',
        '#FF9900',
        '#6C436C',
        '#efad13',
        '#88ef13',
        '#13efef',
        '#3713ef',
        '#8513ef',
        '#ef13ce',
    ]

    useEffect(() => {
        loadEquipment(id);
    }, [id]);

    checkAuth(getUserType(), WEB_PAGE) && navigate('/');

    const loadEquipment = async (equipmentId) => {
        const result = await equipmentService.getEquipmentById(equipmentId);
        setEquipment(result.data);
    }

    const handleSubmit = async (formData) => {
        setLoading(true);
        const result = await pathianDataApiService.getEnergyReportData(formData);
        if (result.error) {
            // setErr(result.message);
            console.log('err', result.message)
        } else {
            setReportStartDate(formData.reportStartDate);
            setReportEndDate(formData.reportEndDate);

            if (result.data.length === 0)
            {
                toast({
                    type: 'warning',
                    description: 'No data found for the selected date range',
                    animation: 'fade down',
                    time: 5000,
                });
            }

            const normalizedData = normalizeDataSeries(result.data);
            setEnergyReportData(normalizedData);
        }
        setLoading(false);
    }

    function normalizeDataSeries(data) {
        const allKeys = new Set();
        data.forEach(record => {
            record.charts.forEach(chart => {
                chart.points.forEach(point => {
                    point.reportData.forEach(dataPoint => allKeys.add(dataPoint.key));
                    point.benchmarkData.forEach(dataPoint => allKeys.add(dataPoint.key));
                })

                const sortedKeys = Array.from(allKeys).sort((a, b) => a - b);

                chart.points.forEach(point => {
                    const reportDataKeyMap1 = new Map(point.reportData.map(item => [item.key, item.value1]));
                    const reportDataKeyMap2 = new Map(point.reportData.map(item => [item.key, item.value2]));
                    point.reportData.map(_ => {
                        return sortedKeys.map(key => ({
                                key,
                                value1: reportDataKeyMap1.get(key) || null,
                                value2: reportDataKeyMap2.get(key) || null
                        }));
                    })

                    const benchmarkDataKeyMap1 = new Map(point.benchmarkData.map(item => [item.key, item.value1]));
                    const benchmarkDataKeyMap2 = new Map(point.benchmarkData.map(item => [item.key, item.value2]));
                    point.benchmarkData.map(_ => {
                        return sortedKeys.map(key => ({
                            key,
                            value1: benchmarkDataKeyMap1.get(key) || null,
                            value2: benchmarkDataKeyMap2.get(key) || null,
                        }));
                    })
                })
            })
        });

        return data;
    }

    return (
        <div style={{marginTop: '2%', marginRight: '20px', marginBottom: '50px'}}>
            <Dimmer active={loading}>
                <Loader size="massive" active={loading}></Loader>
            </Dimmer>
            <h2>{equipment.name}</h2>
            <EquipmentReportForm equipmentId={id} onSubmit={handleSubmit}></EquipmentReportForm>
            {energyReportData.map((data, index) => {
                let firstChartType = "";
                let secondChartType = "";
                switch(data.type) {
                    case "Benchmark":
                        firstChartType = "-Report";
                        secondChartType = "-Benchmark";
                        break;
                    case "Ideal":
                        firstChartType = "-Reported";
                        secondChartType = "-Ideal";
                        break;
                }
                return (
                    <div key={`reportData-${index}`}>
                        <h3>{data.tableSectionTitle}</h3>
                        {data.tableData.map((tableData, tableIndex) => (
                            <div key={`tableData-${tableIndex}`}>
                                <h4>{tableData.name}</h4>
                                <Grid
                                    data={tableData || []}
                                    style={{marginTop: '10px', marginBottom: '10px'}}
                                    pageable={false}
                                    total={tableData.data.length || 0}
                                >
                                    <Column
                                        title={`${moment(reportStartDate).format('MM/DD/YYYY')} - ${moment(reportEndDate).format('MM/DD/YYYY')}`}
                                        field={"name"}
                                        headerCell={EquipmentReportTableHeaderCell}
                                    />
                                    <Column
                                        title="Low Temp Heating|<20°F"
                                        field={"data1"}
                                        headerCell={EquipmentReportTableHeaderCell}
                                        cell={EquipmentReportTableCell}
                                    />
                                    <Column
                                        title="Stage-2 Heating|20-39 Deg. F"
                                        field={"data2"}
                                        headerCell={EquipmentReportTableHeaderCell}
                                        cell={EquipmentReportTableCell}
                                    />
                                    <Column
                                        title="Stage-1 Heating|40-49 Deg. F"
                                        field={"data3"}
                                        headerCell={EquipmentReportTableHeaderCell}
                                        cell={EquipmentReportTableCell}
                                    />
                                    <Column
                                        title="Free Cooling|50-64 Deg. F"
                                        field={"data4"}
                                        headerCell={EquipmentReportTableHeaderCell}
                                        cell={EquipmentReportTableCell}
                                    />
                                    <Column
                                        title="Mech-Cool Stage-1|65-74 Deg. F"
                                        field={"data5"}
                                        headerCell={EquipmentReportTableHeaderCell}
                                        cell={EquipmentReportTableCell}
                                    />
                                    <Column
                                        title="Mech-Cool Stage-2|>74 Deg. F"
                                        field={"data6"}
                                        headerCell={EquipmentReportTableHeaderCell}
                                        cell={EquipmentReportTableCell}
                                    />
                                    <Column
                                        title="Average for Report Period"
                                        field={"total"}
                                        headerCell={EquipmentReportTableHeaderCell}
                                        cell={EquipmentReportTableCell}
                                    />
                                </Grid>
                            </div>
                        ))
                        }
                        <div style={{display: 'flex', flexWrap: 'wrap', flexDirection: 'row'}}>
                            {data.charts.map((chart, chartIndex) => {
                                const comparisonChartMin = Math.min(...chart.points.flatMap(series => series.reportData.concat(series.benchmarkData).map(point => point.value1)));
                                const comparisonChartMax = Math.max(...chart.points.flatMap(series => series.reportData.concat(series.benchmarkData).map(point => point.value1)));
                                return (
                                    <div key={`reportData-${index}-chart-${chartIndex}`} style={{display: 'flex', width: '100%'}}>
                                        <Chart style={{flex: '50%'}}>
                                            <ChartTitle text={data.type !== "Point" ? "Comparison" : "Point Values"}/>
                                            <ChartCategoryAxis>
                                                <ChartCategoryAxisItem
                                                    title={{text: `${chart.labelX} (${chart.unitX})`}}
                                                    labels={{step: 5}}/>
                                            </ChartCategoryAxis>
                                            <ChartValueAxis>
                                                <ChartValueAxisItem title={{text: `${chart.labelY} (${chart.unitY})`}}
                                                                    min={Math.round(comparisonChartMin - 10)}
                                                                    max={Math.round(comparisonChartMax + 10)}/>
                                            </ChartValueAxis>
                                            <ChartSeries>
                                                {
                                                    data.type !== "Point" ? (
                                                        chart.points.map((series, index) => (
                                                            [
                                                                <ChartSeriesItem key={`${index}-report`} type="line"
                                                                                 data={series.reportData} field={"value1"}
                                                                                 categoryField={"key"}
                                                                                 name={`${series.pointCodeOrFnName}${firstChartType}`}
                                                                                 markers={{visible: false}}
                                                                                 tooltip={{visible: true}} dashType={"solid"}
                                                                                 color={colors[index]}/>,
                                                                <ChartSeriesItem key={`${index}-benchmark`} type="line"
                                                                                 data={series.benchmarkData}
                                                                                 field={"value1"}
                                                                                 categoryField={"key"}
                                                                                 name={`${series.pointCodeOrFnName}${secondChartType}`}
                                                                                 markers={{visible: false}}
                                                                                 tooltip={{visible: true}} dashType={"dash"}
                                                                                 color={colors[index]}/>
                                                            ]
                                                        ))
                                                    ) : (
                                                        chart.points.map((series, index) => (
                                                            [
                                                                <ChartSeriesItem key={`${index}-report`} type="line"
                                                                                 data={series.reportData} field={"value1"}
                                                                                 categoryField={"key"}
                                                                                 name={`${series.pointCodeOrFnName}${firstChartType}`}
                                                                                 markers={{visible: false}}
                                                                                 tooltip={{visible: true}} dashType={"solid"}
                                                                                 color={colors[index]}/>
                                                            ]
                                                        ))
                                                    )
                                                }
                                            </ChartSeries>
                                            <ChartLegend position="top" orientation="horizontal"/>
                                        </Chart>
                                        {data.type !== "Point" && (
                                            <Chart style={{flex: '50%'}}>
                                                <ChartTitle text={"Difference"}/>
                                                <ChartCategoryAxis>
                                                    <ChartCategoryAxisItem visible={true}
                                                                           line={{color: 'black'}}
                                                                           labels={{visible: false}}
                                                                           majorTicks={{visible: false}}/>
                                                    <ChartCategoryAxisItem
                                                        title={{text: `${chart.labelX} (${chart.unitX})`}}
                                                        axisCrossingValue={[0, Number.NEGATIVE_INFINITY]}
                                                        labels={{step: 5}}/>
                                                </ChartCategoryAxis>
                                                <ChartValueAxis>
                                                    <ChartValueAxisItem title={{text: `${chart.labelY} (${chart.unitY})`}}
                                                                        axisCrossingValue={[0, Number.NEGATIVE_INFINITY]}/>
                                                </ChartValueAxis>
                                                <ChartSeries>
                                                    {
                                                        chart.points.map((series, index) => (
                                                            [
                                                                <ChartSeriesItem key={index} type="line"
                                                                                 data={series.differenceData}
                                                                                 field={"value1"} categoryField={"key"}
                                                                                 name={`${series.pointCodeOrFnName} Difference`}
                                                                                 markers={{visible: false}}
                                                                                 tooltip={{visible: true}}
                                                                                 color={colors[index]}/>
                                                            ]
                                                        ))
                                                    }
                                                </ChartSeries>
                                                <ChartLegend position="top" orientation="horizontal"/>
                                            </Chart>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                );
            })}
            <div style={toastSection}>
                <SemanticToastContainer position="bottom-center"/>
            </div>
        </div>
    );
};

const EquipmentReportTableCell = (props) => {
    if (!props.dataItem[props.field]) {
        return (<td></td>);
    }

    let value;
    switch (props.dataItem.type) {
        case 1: //percent
            value = `${(props.dataItem[props.field] * 100).toFixed(2)}%`;
            break;
        case 2: //currency
            const usd = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            });
            value = `${usd.format(props.dataItem[props.field])}`;
            break;
        default:
            const decimal = new Intl.NumberFormat('en-US', {
                style: 'decimal',
                maximumFractionDigits: 2,
            })
            value = `${decimal.format(props.dataItem[props.field])}`;
    }

    return (
        <td style={{textAlign: 'right'}}>
            <span>{value}</span>
        </td>
    )
}

const EquipmentReportTableHeaderCell = (props) => {
    const titleSegments = props.title.split('|');

    return (
        <div style={{display: 'flex', flexDirection: 'column', textAlign: 'center', whiteSpace: 'pre-wrap'}}>
            <div>{titleSegments[0]}</div>
            <div>{titleSegments[1]}</div>
        </div>
    )
}
